import type { DataAttributeDto, FieldListDto } from '@bk/contracts';

/** Metadati condivisi dalle liste del modello e dalle proiezioni dell'archivio. */
export type FieldShape = Partial<
  Pick<
    DataAttributeDto,
    'tipo' | 'forma' | 'formato' | 'elenco' | 'unita' | 'multiplo' | 'riferisce' | 'verso'
  >
>;
export type ColumnFilter = { text?: string; value?: string; min?: string; max?: string };
export type Sort = { key: string; direction: 'asc' | 'desc' };
export const foldValue = (v: unknown) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
export const fieldKind = (a?: FieldShape) => a?.forma || a?.formato || a?.tipo || 'testo';
export const numericField = (a?: FieldShape) =>
  ['numero', 'importo', 'quantita', 'anno'].includes(fieldKind(a));
export const rangeField = (a?: FieldShape) =>
  numericField(a) || ['data', 'data_ora', 'ora'].includes(fieldKind(a));
export const choiceField = (a?: FieldShape) =>
  ['scelta', 'scelte', 'si_no', 'collegamento'].includes(fieldKind(a)) ||
  (!!a?.elenco && fieldKind(a) !== 'quantita') ||
  !!a?.riferisce;
export const selectedValues = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.map(String)
    : String(v ?? '')
        .split(/\s*;\s*/)
        .filter(Boolean);

/** I dati salvati usano il punto decimale; i dati italiani con virgola sono accettati senza perdere le unità. */
export function numericValue(v: unknown): { number: number; unit: string } | null {
  if (typeof v === 'number') return Number.isFinite(v) ? { number: v, unit: '' } : null;
  const m = /^\s*([€$£]?)\s*([+-]?(?:\d[\d.,]*|\.\d+))\s*([^\d]*)$/.exec(String(v ?? ''));
  if (!m) return null;
  const raw = m[2]!;
  const number = Number(raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw);
  return Number.isFinite(number) ? { number, unit: (m[1] || m[3] || '').trim() } : null;
}

/** Nei controlli Min/Max il punto raggruppa le migliaia, la virgola separa i decimali. */
export function filterNumber(v: string): number | null {
  const raw = v.trim().replace(/\s/g, '');
  if (!/^[+-]?(?:\d+|\d{1,3}(?:\.\d{3})+)(?:,\d+)?$/.test(raw)) return null;
  const n = Number(raw.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Le date con ora senza fuso del modello sono ore italiane, anche su un dispositivo in un altro fuso. */
function dateTimeValue(value: string): number {
  if (/^\d{4}-\d{2}-\d{2}/.test(value) && dateValue(value) === null) return NaN;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value))
    return new Date(value).getTime();
  const utc = new Date(`${value}Z`).getTime();
  if (Number.isNaN(utc)) return utc;
  const offsetAt = (time: number) => {
    const zone = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Rome',
      timeZoneName: 'shortOffset',
    })
      .formatToParts(time)
      .find((p) => p.type === 'timeZoneName')?.value;
    const m = /GMT([+-])(\d+)(?::(\d+))?/.exec(zone ?? '');
    return m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) * 60_000 : 0;
  };
  return utc - offsetAt(utc - offsetAt(utc));
}

function dateValue(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return null;
  const day = value.slice(0, 10);
  const parsed = new Date(`${day}T12:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : null;
}

export function fieldLabel(
  a: FieldShape | undefined,
  v: unknown,
  lists?: Map<string, FieldListDto>,
): string {
  if (v === null || v === undefined || v === '') return '';
  const kind = fieldKind(a);
  if (typeof v === 'boolean' || kind === 'si_no') {
    if (v === true || v === 'true') return 'Sì';
    if (v === false || v === 'false') return 'No';
  }
  const list = a?.elenco ? lists?.get(a.elenco) : undefined;
  if (choiceField(a) || Array.isArray(v))
    return selectedValues(v)
      .map((x) => list?.voci.find((o) => o.valore === x)?.etichetta ?? x)
      .join(', ');
  if (numericField(a) || (!(a?.forma || a?.formato || a?.tipo) && typeof v === 'number')) {
    const parsed = numericValue(v);
    if (!parsed) return String(v);
    if (kind === 'anno') return String(parsed.number);
    const n = new Intl.NumberFormat('it-IT', {
      useGrouping: true,
      minimumFractionDigits: kind === 'importo' ? 2 : 0,
      maximumFractionDigits: 20,
    }).format(parsed.number);
    const unit = parsed.unit || a?.unita || (kind === 'importo' ? '€' : '');
    return `${n}${unit ? ` ${unit}` : ''}`;
  }
  if (kind === 'data' || kind === 'data_ora') {
    const raw = String(v);
    if (dateValue(raw)) {
      const date =
        kind === 'data_ora'
          ? new Date(dateTimeValue(raw))
          : new Date(raw.length === 10 ? `${raw}T12:00:00` : raw);
      if (!Number.isNaN(date.getTime()))
        return kind === 'data'
          ? date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
          : date.toLocaleString('it-IT', {
              timeZone: 'Europe/Rome',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
    }
  }
  if (kind === 'ora' && /^\d{2}:\d{2}/.test(String(v))) return String(v).slice(0, 5);
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function comparable(a: FieldShape, v: unknown): string | number | null {
  if (v === null || v === undefined || v === '') return null;
  if (numericField(a)) return numericValue(v)?.number ?? null;
  if (fieldKind(a) === 'data_ora') {
    const n = dateTimeValue(String(v));
    return Number.isNaN(n) ? null : n;
  }
  if (fieldKind(a) === 'ora') return /^\d{2}:\d{2}/.test(String(v)) ? String(v).slice(0, 5) : null;
  if (fieldKind(a) === 'data') return dateValue(String(v));
  return String(v);
}

function rangeBound(a: FieldShape, value: string, upper: boolean) {
  if (numericField(a))
    return fieldKind(a) === 'anno'
      ? /^\d{4}$/.test(value)
        ? Number(value)
        : null
      : filterNumber(value);
  const parsed = comparable(a, value);
  // Il controllo dell'ora arriva al minuto: «Al» comprende anche i secondi di quel minuto.
  return fieldKind(a) === 'data_ora' && typeof parsed === 'number' && upper && value.length === 16
    ? parsed + 59_999
    : parsed;
}

/** Vuoti e dati non confrontabili restano in fondo anche nell'ordine decrescente. */
export function compareFields(
  a: FieldShape,
  left: unknown,
  right: unknown,
  direction: Sort['direction'],
  lists?: Map<string, FieldListDto>,
): number {
  const l = comparable(a, left),
    r = comparable(a, right);
  if (l === null || r === null) return l === r ? 0 : l === null ? 1 : -1;
  const cmp =
    typeof l === 'number' && typeof r === 'number'
      ? l - r
      : fieldLabel(a, left, lists).localeCompare(fieldLabel(a, right, lists), 'it', {
          sensitivity: 'base',
          numeric: true,
        });
  // Le date si confrontano nel formato canonico, mai nel testo italiano visualizzato.
  const ordered = ['data', 'ora'].includes(fieldKind(a)) ? String(l).localeCompare(String(r)) : cmp;
  return direction === 'desc' ? -ordered : ordered;
}

export function matchesField(
  a: FieldShape,
  v: unknown,
  f: ColumnFilter,
  lists?: Map<string, FieldListDto>,
): boolean {
  if (f.value && !selectedValues(v).includes(f.value)) return false;
  if (f.text && !foldValue(fieldLabel(a, v, lists)).includes(foldValue(f.text.trim())))
    return false;
  if (!f.min && !f.max) return true;
  const n = comparable(a, v);
  if (n === null) return false;
  for (const [bound, lower] of [
    [f.min, true],
    [f.max, false],
  ] as const) {
    if (!bound) continue;
    const b = rangeBound(a, bound, !lower);
    if (b === null || (lower ? n < b : n > b)) return false;
  }
  return true;
}

export const archiveKind = (a?: FieldShape): 'testo' | 'numero' | 'data' | 'data_ora' | 'ora' =>
  numericField(a)
    ? 'numero'
    : ['data', 'data_ora', 'ora'].includes(fieldKind(a))
      ? (fieldKind(a) as 'data' | 'data_ora' | 'ora')
      : 'testo';

/** Traduce i filtri di colonna per la ricerca paginata dell'archivio. */
export function archiveFilters(
  fields: Array<FieldShape & { nome: string }>,
  filters: Record<string, ColumnFilter>,
) {
  return fields.flatMap((a) => {
    const f = filters[a.nome];
    if (!f) return [];
    const out: Array<{
      attributo: string;
      operatore: 'uguale' | 'contiene' | 'include' | 'maggiore_uguale' | 'minore_uguale';
      valore: string;
      tipo_valore?: ReturnType<typeof archiveKind>;
    }> = [];
    if (f.value)
      out.push({
        attributo: a.nome,
        operatore: a.multiplo || fieldKind(a) === 'scelte' ? 'include' : 'uguale',
        valore: f.value,
      });
    if (f.text) out.push({ attributo: a.nome, operatore: 'contiene', valore: f.text.trim() });
    for (const [v, op] of [
      [f.min, 'maggiore_uguale'],
      [f.max, 'minore_uguale'],
    ] as const) {
      if (!v) continue;
      const value = rangeBound(a, v, op === 'minore_uguale');
      // Un valore non valido non diventa un filtro assente.
      out.push({
        attributo: a.nome,
        operatore: op,
        tipo_valore: archiveKind(a),
        valore:
          value === null
            ? '__valore_non_valido__'
            : fieldKind(a) === 'data_ora'
              ? new Date(value).toISOString()
              : String(value),
      });
    }
    return out;
  });
}
